import React, { useEffect, useState } from "react";
import "./Address.scss";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useToast } from "../../Contexts/ToastContext";

export function MobAddress() {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get("edit") || null;

    const [formData, setFormData] = useState({
        name: "",
        mobile: "",
        pincode: "",
        streetAddress: "",
        city: "",
        state: "",
        addressType: "HOME",
        isDefault: false,
    });

    const mobilePattern = /^\d{10}$/;
    const pincodePattern = /^[1-9][0-9]{5}$/;

    const [errorData, setErrorData] = useState({
        mobile: false,
        pincode: false
    });

    const [submitClicked, setSubmitClicked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingAddressId, setEditingAddressId] = useState(null);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [deletingAddress, setDeletingAddress] = useState(false);

    useEffect(() => {
        if (!editId) return;
        const token = localStorage.getItem("token");
        if (!token) return;

        let cancelled = false;
        axios.get("/address/get-all", { headers: { Authorization: `Bearer ${token}` } })
            .then((response) => {
                if (cancelled) return;
                const addr = (response.data || []).find(
                    (a) => a._id === editId || String(a._id) === editId
                );
                if (!addr) return;
                setFormData({
                    name: addr.name || "",
                    mobile: addr.mobile || "",
                    pincode: addr.pincode != null ? String(addr.pincode) : "",
                    streetAddress: addr.streetAddress || "",
                    city: addr.city || "",
                    state: addr.state || "",
                    addressType: addr.addressType === "OFFICE" ? "OFFICE" : "HOME",
                    isDefault: !!addr.isDefault,
                });
                setEditingAddressId(addr._id);
            })
            .catch((error) => {
                console.error("Error loading address:", error);
                showToast({ message: "Failed to load address details." });
            });

        return () => { cancelled = true; };
    }, [editId, showToast]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;

        if (submitClicked && id === "mobile") {
            setErrorData((prev) => ({ ...prev, mobile: !mobilePattern.test(value) }));
        } else if (submitClicked && id === "pincode") {
            setErrorData((prev) => ({ ...prev, pincode: !pincodePattern.test(value) }));
        }
        setFormData((prevData) => ({ ...prevData, [id]: value }));
    };

    const handleAddressTypeChange = (type) => {
        setFormData((prevData) => ({ ...prevData, addressType: type }));
    };

    const toggleDefaultAddress = () => {
        setFormData((prevData) => ({ ...prevData, isDefault: !prevData.isDefault }));
    };

    const openDeleteConfirmModal = () => setShowDeleteConfirmModal(true);
    const closeDeleteConfirmModal = () => {
        if (!deletingAddress) setShowDeleteConfirmModal(false);
    };

    const handleConfirmDeleteAddress = async () => {
        if (!editingAddressId) return;
        const token = localStorage.getItem("token");
        if (!token) return;

        setDeletingAddress(true);
        try {
            await axios.delete(`/address/${editingAddressId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setShowDeleteConfirmModal(false);
            showToast({ message: "Address deleted successfully." });
            navigate("/order-summary");
        } catch (error) {
            console.error("Error deleting address:", error);
            showToast({ message: error.response?.data?.message || "Failed to delete address." });
        } finally {
            setDeletingAddress(false);
        }
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;

        setSubmitClicked(true);

        if (!formData.name || !formData.mobile || !formData.pincode || !formData.streetAddress) {
            showToast({ message: "Please fill in all required fields" });
            return;
        }

        if (!mobilePattern.test(formData.mobile)) {
            setErrorData((prev) => ({ ...prev, mobile: true }));
            return;
        }

        if (!pincodePattern.test(formData.pincode)) {
            setErrorData((prev) => ({ ...prev, pincode: true }));
            return;
        }

        const token = localStorage.getItem("token");
        if (!token) {
            showToast({ message: "Please log in to save your address." });
            return;
        }

        setIsSubmitting(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const payload = {
                name: formData.name,
                mobile: formData.mobile,
                pincode: formData.pincode,
                streetAddress: formData.streetAddress,
                city: formData.city,
                state: formData.state,
                addressType: formData.addressType,
                isDefault: formData.isDefault
            };

            if (editingAddressId) {
                await axios.patch(`/address/${editingAddressId}`, payload, config);
            } else {
                await axios.post("/address/create", payload, config);
            }

            navigate("/order-summary");
        } catch (error) {
            console.error("Error saving address:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to save address. Please try again.";
            showToast({ message: errorMessage });
        } finally {
            setIsSubmitting(false);
        }
    };

    const isBusy = isSubmitting || deletingAddress;

    return (
        <div className="mob-checkout-scaling-root">
            <div className="mob-address-container">
                <div className="mob-address-card">
                    <div className="mob-address-header">
                        <h2>{editingAddressId ? "Edit Delivery Address" : "Add Delivery Address"}</h2>
                        <p>Enter your delivery details to complete your order</p>
                    </div>

                    <div className="mob-address-form">
                        <div className="mob-address-section">
                            <div className="mob-section-header">
                                <h3>Contact Details</h3>
                            </div>
                            <div className="mob-form-group">
                                <label htmlFor="name" className="mob-input-label">Name *</label>
                                <input
                                    className={`mob-form-input ${!formData.name && submitClicked ? 'error' : ''}`}
                                    id="name"
                                    type="text"
                                    placeholder="Enter your full name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    disabled={isBusy}
                                />
                            </div>
                            <div className="mob-form-group">
                                <label htmlFor="mobile" className="mob-input-label">Mobile Number *</label>
                                <input
                                    className={`mob-form-input ${errorData.mobile ? 'error' : ''}`}
                                    id="mobile"
                                    type="tel"
                                    placeholder="Enter your 10-digit mobile number"
                                    value={formData.mobile}
                                    onChange={handleInputChange}
                                    maxLength="10"
                                    disabled={isBusy}
                                />
                                {errorData.mobile && (
                                    <div className="mob-error-message">Invalid Mobile Number</div>
                                )}
                            </div>
                        </div>

                        <div className="mob-address-section">
                            <div className="mob-section-header">
                                <h3>Address</h3>
                            </div>
                            <div className="mob-form-group">
                                <label htmlFor="pincode" className="mob-input-label">Pin Code *</label>
                                <input
                                    className={`mob-form-input ${errorData.pincode ? 'error' : ''}`}
                                    id="pincode"
                                    type="tel"
                                    placeholder="Enter 6-digit pin code"
                                    value={formData.pincode}
                                    onChange={handleInputChange}
                                    maxLength="6"
                                    disabled={isBusy}
                                />
                                {errorData.pincode && (
                                    <div className="mob-error-message">Invalid Pincode</div>
                                )}
                            </div>
                            <div className="mob-form-group">
                                <label htmlFor="streetAddress" className="mob-input-label">Address (House No, Building, Street, Area) *</label>
                                <input
                                    className={`mob-form-input ${!formData.streetAddress && submitClicked ? 'error' : ''}`}
                                    id="streetAddress"
                                    type="text"
                                    placeholder="Enter your complete address"
                                    value={formData.streetAddress}
                                    onChange={handleInputChange}
                                    disabled={isBusy}
                                />
                            </div>
                            <div className="mob-form-row">
                                <div className="mob-form-group mob-form-group-half">
                                    <label htmlFor="city" className="mob-input-label">City</label>
                                    <input
                                        className="mob-form-input"
                                        id="city"
                                        type="text"
                                        placeholder="City"
                                        value={formData.city}
                                        onChange={handleInputChange}
                                        disabled={isBusy}
                                    />
                                </div>
                                <div className="mob-form-group mob-form-group-half">
                                    <label htmlFor="state" className="mob-input-label">State</label>
                                    <input
                                        className="mob-form-input"
                                        id="state"
                                        type="text"
                                        placeholder="State"
                                        value={formData.state}
                                        onChange={handleInputChange}
                                        disabled={isBusy}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mob-address-section">
                            <div className="mob-section-header">
                                <h3>Save Address As</h3>
                            </div>
                            <div className="mob-address-types">
                                <button
                                    className={`mob-address-type-btn ${formData.addressType === 'HOME' ? 'active' : ''}`}
                                    onClick={() => handleAddressTypeChange('HOME')}
                                    type="button"
                                    disabled={isBusy}
                                >
                                    Home
                                </button>
                                <button
                                    className={`mob-address-type-btn ${formData.addressType === 'OFFICE' ? 'active' : ''}`}
                                    onClick={() => handleAddressTypeChange('OFFICE')}
                                    type="button"
                                    disabled={isBusy}
                                >
                                    Work
                                </button>
                            </div>
                        </div>

                        <div className="mob-address-section">
                            <div className="mob-default-address">
                                <label className="mob-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={formData.isDefault}
                                        onChange={toggleDefaultAddress}
                                        className="mob-checkbox-input"
                                        disabled={isBusy}
                                    />
                                    <span className="mob-checkbox-custom"></span>
                                    <span className="mob-checkbox-text">Make this my default address</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="mob-address-footer">
                        <button
                            type="button"
                            className="mob-save-address-btn"
                            onClick={handleSubmit}
                            disabled={isBusy}
                        >
                            {isSubmitting
                                ? (editingAddressId ? "SAVING..." : "ADDING...")
                                : (editingAddressId ? "SAVE ADDRESS" : "ADD ADDRESS")}
                        </button>
                        {editingAddressId && (
                            <button
                                type="button"
                                className="mob-delete-address-btn"
                                onClick={openDeleteConfirmModal}
                                disabled={isBusy}
                            >
                                Delete address
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {showDeleteConfirmModal && (
                <div className="confirm-modal-overlay" onClick={closeDeleteConfirmModal}>
                    <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="confirm-modal-header">
                            <h3>Delete address</h3>
                            <button type="button" className="confirm-modal-close" onClick={closeDeleteConfirmModal} disabled={deletingAddress} aria-label="Close">×</button>
                        </div>
                        <div className="confirm-modal-body">
                            <p>Are you sure you want to delete this address?</p>
                        </div>
                        <div className="confirm-modal-footer">
                            <button type="button" className="confirm-btn-secondary" onClick={closeDeleteConfirmModal} disabled={deletingAddress}>Cancel</button>
                            <button type="button" className="confirm-btn-danger" onClick={handleConfirmDeleteAddress} disabled={deletingAddress}>
                                {deletingAddress ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
